import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button, { type ButtonProps } from '@mui/material/Button';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type MouseEvent, useCallback, useId, useState } from 'react';

type ItemWithID<V> = {
  [P in keyof V]: V[P];
} & { id: string };

type CustomMenuProps<V, T extends ItemWithID<V>> = Readonly<{
  items: T[];
  value: T['id'];
  renderLabel: (item: T['id']) => React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
  menuProps?: Omit<MenuProps, 'children' | 'open' | 'onClose' | 'anchorEl'>;
  buttonProps?: ButtonProps;
  onSelect?: (item: T) => void;
}>;
export default function CustomMenu<V, T extends ItemWithID<V>>({
  items,
  renderItem,
  renderLabel,
  value,
  buttonProps,
  menuProps,
  onSelect,
}: CustomMenuProps<V, T>) {
  const id = useId();
  const generatedButtonID = useId();
  // A caller id must win on the button and still feed the menu's aria-labelledby, hence re-applied after the spread.
  const buttonID = buttonProps?.id ?? generatedButtonID;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = useCallback(
    (item: T) => {
      handleClose();
      onSelect?.(item);
    },
    [onSelect]
  );

  return (
    <>
      <Button
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        onClick={handleMenuOpen}
        {...buttonProps}
        id={buttonID}
      >
        {renderLabel(value)}
      </Button>
      <Menu id={id} aria-labelledby={buttonID} anchorEl={anchorEl} open={open} onClose={handleClose} {...menuProps}>
        {items.map((item) => (
          <MenuItem
            key={item.id}
            dense
            selected={item.id === value}
            onClick={() => {
              handleSelect(item);
            }}
          >
            {renderItem(item)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
